import { useCallback, useState } from 'react';

import { MAX_RECENT_ITEMS, RECENTS_STORAGE_PREFIX } from '../constants';
import type { IRecentItem } from '../types';

export interface UseRecentItemsResult {
    readonly recents: readonly IRecentItem[];
    addRecent: (item: IRecentItem) => void;
    removeRecent: (id: string) => void;
    clearRecents: () => void;
}

function storageKey(orgId: string): string {
    return `${RECENTS_STORAGE_PREFIX}${orgId}`;
}

function readRecents(orgId: string): IRecentItem[] {
    try {
        const raw = localStorage.getItem(storageKey(orgId));
        if (!raw) return [];
        const parsed: unknown = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter(
            (r): r is IRecentItem =>
                typeof r === 'object' && r !== null && 'id' in r && 'url' in r && 'title' in r,
        );
    } catch {
        return []; // corrupt storage — treat as empty rather than crash the palette
    }
}

function writeRecents(orgId: string, items: readonly IRecentItem[]): void {
    try {
        localStorage.setItem(storageKey(orgId), JSON.stringify(items));
    } catch {
        // quota/full — recents are best-effort
    }
}

/** Last-N opened results, persisted per org in localStorage. */
export function useRecentItems(orgId: string): UseRecentItemsResult {
    const [recents, setRecents] = useState<readonly IRecentItem[]>(() => readRecents(orgId));

    const addRecent = useCallback(
        (item: IRecentItem) => {
            setRecents((prev) => {
                const next = [item, ...prev.filter((r) => r.id !== item.id)].slice(
                    0,
                    MAX_RECENT_ITEMS,
                );
                writeRecents(orgId, next);
                return next;
            });
        },
        [orgId],
    );

    const removeRecent = useCallback(
        (id: string) => {
            setRecents((prev) => {
                const next = prev.filter((r) => r.id !== id);
                writeRecents(orgId, next);
                return next;
            });
        },
        [orgId],
    );

    const clearRecents = useCallback(() => {
        setRecents([]);
        writeRecents(orgId, []);
    }, [orgId]);

    return { recents, addRecent, removeRecent, clearRecents };
}
